
import { useState, useEffect, useRef } from "react";
import {
  connectDevice,
  disableBpmVariance,
  forceSimpleHeartbeatMode,
  sendParameters,
  disconnectDevice,
  onBleDisconnect,
  readFirmwareInfo,
  readParameters
} from "./ble";
import ThickSlider from "./components/ThickSlider";
import HeartbeatSurveyWizard from "./components/HeartbeatSurveyWizard";
import HeartbeatCognitiveBattery from "./components/HeartbeatCognitiveBattery";
import "./App.css";
import FullMigration from "./Migration";

const SIMPLE_MODE_FW_ID = 10003;
const BLE_WRITE_DELAY_MS = 100;

export default function App() {
  const [mode, setMode] = useState(null);

  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [settingsUpdating, setSettingsUpdating] = useState(false);
  const [firmwareInfo, setFirmwareInfo] = useState(null);

  const [bpm, setBpm] = useState(54);
  const [amplitude, setAmplitude] = useState(50); // 0–100 %
  const [breathingRate, setBreathingRate] = useState(33);
  const [breathingIntensity, setBreathingIntensity] = useState(20);

  const [varianceS1S1ms, setVarianceS1S1ms] = useState(10);
  const [varianceS1S2, setVarianceS1S2] = useState(0);

  const [toast, setToast] = useState(null);
  const [toastVisible, setToastVisible] = useState(false);
  const toastTimerRef = useRef(null);

  const showVariance = new URLSearchParams(window.location.search).get("id") === "research";

  useEffect(() => {
    onBleDisconnect(() => {
      setConnected(false);
      setConnecting(false);
      setFirmwareInfo(null);
      alert("Connection to device lost");
    });
  }, []);

  function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  function bpmFromDtMs(dtMs) {
    return Math.round(60000 / dtMs);
  }

  function amplitudePctFromAmp(amp) {
    return Math.round(amp * 100);
  }

  async function handleDisconnect() {
    await disconnectDevice();
    setConnected(false);
    setSettingsUpdating(false);
    setFirmwareInfo(null);
  }

  async function handleConnect() {
    try {
      setConnecting(true);

      await connectDevice();
      const info = await readFirmwareInfo();
      setFirmwareInfo(info);

      const params = await readParameters();
      if (params) {
        setBpm(bpmFromDtMs(params.dtS1S1));
        setAmplitude(amplitudePctFromAmp(params.amp));
        setBreathingRate(params.ledBpm);
        setBreathingIntensity(params.ledIntensity);

        if (showVariance) {
          setVarianceS1S1ms(Math.round(params.dtS1S1 * params.varS1S1));
          setVarianceS1S2(0);
        }
      }

      setConnected(true);
    } catch (err) {
      console.error("BLE connection failed:", err);
      alert("Failed to connect to device");
    } finally {
      setConnecting(false);
    }
  }

  function showToast(msg) {
    setToast(msg);
    setToastVisible(false);

    requestAnimationFrame(() => {
      setToastVisible(true);
    });

    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);

    toastTimerRef.current = setTimeout(() => {
      setToastVisible(false);
      setTimeout(() => setToast(null), 250);
    }, 1800);
  }

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  async function handleSend() {
    try {
      setSettingsUpdating(true);

      if (Number(firmwareInfo?.fw_id) === SIMPLE_MODE_FW_ID) {
        await forceSimpleHeartbeatMode();
        await sleep(BLE_WRITE_DELAY_MS);
      }

      await sendParameters({
        bpm,
        varianceS1S1:
          showVariance && varianceS1S1ms > 0 ? (varianceS1S1ms * bpm) / 60000.0 : 0,
        varianceS1S2: showVariance && varianceS1S2 > 0 ? varianceS1S2 / 100.0 : 0,
        amplitudePct: amplitude,
        ledBpm: breathingRate,
        ledIntensity: breathingIntensity
      });

      await sleep(BLE_WRITE_DELAY_MS);
      await disableBpmVariance();

      showToast("Settings updated");
    } catch (err) {
      console.error("Failed to update settings:", err);
      showToast("Update failed - please try again");
    } finally {
      setSettingsUpdating(false);
    }
  }

  return (
    <div className="app">
      {mode === null && (
        <div className="mode-select-screen">
          <div>
            <img
              src="logo_no_neurotech.png"
              alt="LYEONS Logo"
              className="lyeons-logo"
            />

            <h2 className="title" style={{marginTop:"10px"}}>WELCOME</h2>

            <p className="subtitle" style={{maxWidth: 420, marginBottom:"0px", marginTop:"30px"}}>
              Welcome to the LYEONS web app.<br /><br />We are exploring how supporting physiological calmness may support you to feel more balanced in your everyday life. Please select from the options below.
              </p>
          </div>

          <button className="mode-select-button" onClick={() => setMode("survey")}>
            PARTICIPATE IN HEARTBEAT STUDY
          </button>

          <button className="mode-select-button" onClick={() => setMode("personalise")}>
            PERSONALISE YOUR HEART
          </button>

          <button className="mode-select-button" onClick={() => setMode("ota")}>
            FIRMWARE UPDATE
          </button>
          {/* <button className="mode-select-button" onClick={() => setMode("cogtest")}>
  TEST: HEARTBEAT + GAMES
</button> */}
        </div>
      )}

      {mode === "survey" && <HeartbeatSurveyWizard onExit={() => setMode(null)} />}

      {mode === "cogtest" && <HeartbeatCognitiveBattery onExit={() => setMode(null)} />}


      {mode === "ota" && <FullMigration 
        onExit={() => setMode(null)}
        onGoPersonalise={() => setMode("personalise")} 
      />}

      {mode === "personalise" && (
        <>
          {connecting && (
            <div className="connecting-screen">
              <div className="spinner" />
              <p>Connecting to device…</p>
            </div>
          )}

          {!connecting && (
            <>
              {!connected && (
                <div className="connect-screen">
                  <h2 className="title">PERSONALISE YOUR HEART</h2>

                  <p className="subtitle">
                    This page is for LYEONS users who
                    want to personalise heartbeat settings. The Android app is available now, and
                    iOS is coming soon.
                  </p>

                  <img
                    src="lyeonsheart.png"
                    alt="LYEONS Heart device"
                    className="heart-image"
                  />

                  <div className="subtitle" style={{ textAlign: "left", maxWidth: 420 }}>
                    <p>1. Press the <strong>Connect</strong> button below</p>
                    <p>2. Turn the device on or place it on the dock to enable Bluetooth</p>
                    <p>3. Select the device starting with <strong>LH</strong> and press Pair</p>
                  </div>

                  <button className="connect-btn" onClick={handleConnect}>
                    CONNECT
                  </button>
                </div>
              )}

              {connected && (
                <>
                  <h2 className="title">PERSONALISE YOUR HEART</h2>

                  <div className="heartbeat-player-engine">
                    <ThickSlider
                      label="Heartbeats Per Minute"
                      value={bpm}
                      min={30}
                      max={150}
                      onChange={setBpm}
                    />

                    <ThickSlider
                      label="Heartbeat Strength"
                      value={amplitude}
                      min={1}
                      max={100}
                      onChange={setAmplitude}
                    />

                    {showVariance && (
                      <ThickSlider
                        label="Heartbeat Variance (ms)"
                        value={varianceS1S1ms}
                        min={0}
                        max={80}
                        onChange={setVarianceS1S1ms}
                      />
                    )}

                    <ThickSlider
                      label="Breathing Rate"
                      value={breathingRate}
                      min={10}
                      max={100}
                      onChange={setBreathingRate}
                    />

                    <ThickSlider
                      label="Breathing Intensity"
                      value={breathingIntensity}
                      min={1}
                      max={100}
                      onChange={setBreathingIntensity}
                    />
                  </div>

                  <div className="update-actions">
                    {toast && (
                      <div
                        className={`toast ${toastVisible ? "toast--in" : "toast--out"}`}
                      >
                        {toast}
                      </div>
                    )}

                    <div className="update-buttons">
                      <button className="update-btn" onClick={handleSend} disabled={settingsUpdating}>
                        {settingsUpdating ? "UPDATING" : "UPDATE"}
                      </button>
                      <button className="update-btn" onClick={handleDisconnect} disabled={settingsUpdating}>
                        DISCONNECT
                      </button>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
